/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  BLUR_COMMAND,
  COMMAND_PRIORITY_LOW,
  KEY_ESCAPE_COMMAND,
} from 'lexical';
import { useEffect, useRef } from 'react';
import type { AnyFeature, InsertFn } from './chat-text-field.types';
import { FeatureNode } from './feature.node';

type SetInsert = (fn: InsertFn<readonly AnyFeature[]>) => void;

interface TriggerDetectionPluginProps {
  features: readonly AnyFeature[];
  activeTrigger: string | null;
  triggerQuery: string;
  onTriggerChange: (trigger: string | null, query: string) => void;
  onClose: () => void;
  caretAnchorRef: React.RefObject<HTMLSpanElement | null>;
  popoverRef: React.RefObject<HTMLElement | null>;
  setInsert: SetInsert;
}

/**
 * Lexical plugin that handles trigger detection, caret positioning, and node insertion.
 *
 * Responsibilities:
 * 1. **Trigger detection** — scans text before cursor for registered trigger characters.
 * 2. **Caret positioning** — repositions the zero-size `caretAnchorRef` span to the caret DOM position.
 * 3. **Escape handling** — closes the active trigger state on Escape key.
 * 4. **Insert registration** — provides the editor-aware `insert` function.
 */
function TriggerDetectionPlugin({
  features,
  onTriggerChange,
  onClose,
  caretAnchorRef,
  popoverRef,
  setInsert,
  activeTrigger,
  triggerQuery,
}: TriggerDetectionPluginProps) {
  const [editor] = useLexicalComposerContext();

  // Prevents re-entrant insertions caused by discrete updates triggering nested flushSync.
  const pendingInsertRef = useRef(false);

  useEffect(() => {
    if (activeTrigger === null) {
      pendingInsertRef.current = false;
    }
  }, [activeTrigger]);

  useEffect(() => {
    setInsert(() => (id: string, props: unknown) => {
      if (pendingInsertRef.current) return;
      pendingInsertRef.current = true;

      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        const anchor = selection.anchor;
        const anchorNode = anchor.getNode();
        if (!$isTextNode(anchorNode)) return;

        const offset = anchor.offset;
        const textBeforeCursor = anchorNode.getTextContent().slice(0, offset);

        const feature = features.find((f): f is AnyFeature => f.id === id);
        if (!feature) return;

        const trigger = activeTrigger ?? feature.trigger;
        const query = triggerQuery;

        const triggerIdx = textBeforeCursor.lastIndexOf(trigger);
        if (triggerIdx === -1) return;

        const deleteLength = trigger.length + query.length;
        anchorNode.spliceText(triggerIdx, deleteLength, '');

        const lexicalNode = FeatureNode.create(feature.id, props as Record<string, unknown>);
        const spaceNode = $createTextNode(' ');

        // Avoid leaving an empty text node that Lexical's normalization would remove,
        // which would destroy the decorator's DOM span and cause duplicate rendering.
        if (anchorNode.getTextContent() === '') {
          anchorNode.replace(lexicalNode);
        } else {
          anchorNode.insertAfter(lexicalNode);
        }
        lexicalNode.insertAfter(spaceNode);
        spaceNode.select();
      });

      onClose();
    });
  }, [editor, setInsert, onClose, activeTrigger, triggerQuery, features]);

  useEffect(() => {
    return editor.registerCommand(
      BLUR_COMMAND,
      (event) => {
        const relatedTarget = event?.relatedTarget as Element | null;
        const movingToPopover = popoverRef.current?.contains(relatedTarget) ?? false;
        if (activeTrigger !== null && !movingToPopover) onClose();
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, activeTrigger, onClose, popoverRef]);

  useEffect(() => {
    return editor.registerCommand(
      KEY_ESCAPE_COMMAND,
      () => {
        if (activeTrigger !== null) {
          onClose();
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, activeTrigger, onClose]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          onClose();
          return;
        }

        const anchor = selection.anchor;
        const anchorNode = anchor.getNode();

        if (!$isTextNode(anchorNode)) {
          onClose();
          return;
        }

        const textBeforeCursor = anchorNode.getTextContent().slice(0, anchor.offset);
        let matched = false;

        for (const feature of features) {
          const triggerIdx = textBeforeCursor.lastIndexOf(feature.trigger);
          if (triggerIdx === -1) continue;

          // Only activate when the trigger sits at a word boundary (start of text, or preceded by whitespace).
          const charBefore = textBeforeCursor[triggerIdx - 1];
          const isWordBoundary = triggerIdx === 0 || charBefore === ' ' || charBefore === '\n' || charBefore === '\t';
          if (!isWordBoundary) continue;

          const query = textBeforeCursor.slice(triggerIdx + feature.trigger.length);

          // A space immediately after the trigger character (e.g. "@ name") exits trigger mode.
          if (query.startsWith(' ')) {
            onClose();
            return;
          }

          if (query.endsWith(' ')) {
            // Auto-select if exactly one suggestion remains.
            const items = popoverRef.current?.querySelectorAll<HTMLElement>('[data-key]:not([aria-disabled="true"])');
            if (items?.length === 1 && !pendingInsertRef.current) {
              items[0].click();
              matched = true;
              break;
            }
            // Exit on double-space, keep open on single (allows multi-word names like "Jeff Clark").
            if (query.endsWith('  ')) {
              onClose();
              return;
            }
          }

          updateCaretAnchorRef(caretAnchorRef);

          onTriggerChange(feature.trigger, query);
          matched = true;
          break;
        }

        if (!matched) onClose();
      });
    });
  }, [editor, features, onTriggerChange, onClose, caretAnchorRef, popoverRef]);

  return null;
}

/**
 * Moves the zero-size fixed `<span>` to the current caret position.
 * Uses direct DOM mutation to avoid React re-renders on every keystroke.
 */
function updateCaretAnchorRef(caretAnchorRef: React.RefObject<HTMLSpanElement | null>): void {
  const span = caretAnchorRef.current;
  if (!span) return;

  const domSelection = window.getSelection();
  if (!domSelection || domSelection.rangeCount === 0) return;

  const range = domSelection.getRangeAt(0).cloneRange();
  range.collapse(true);

  const rects = range.getClientRects();
  const rect = rects.length > 0 ? rects[0] : range.getBoundingClientRect();

  span.style.top = `${rect.top}px`;
  span.style.left = `${rect.left}px`;
}

export { TriggerDetectionPlugin };
